/**
 * Входы с доступом к организации (multi-organization-login): вкладка «Учётные данные» карточки
 * клиента-компании у команды. Один вход может быть закреплён за несколькими организациями —
 * сетевик ведёт десятки, у холдинга несколько юрлиц: после входа он выбирает организацию.
 * Закрепляет и открепляет только команда Центрики (клиентам это не отдаём).
 *
 * У каждого входа — представитель организации (login-representative; ТЗ 26.10.2026, п.9.4; ответ
 * В28): контактное лицо организации, его ФИО и должность клиент видит справа вверху. Необязателен.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { teamApi, teamClientApi, type ClientLogin, type Contact } from '@/lib/api'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { getErrorMessage } from '@/lib/errors'
import { Alert } from '@/design-system/components/feedback/alert'
import { Panel } from '@/design-system/components/layout/panel'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'

/** Склонение «ещё N организаций» для бейджа входа, закреплённого за несколькими организациями. */
export function otherOrganizationsLabel(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `ещё ${n} организация`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `ещё ${n} организации`
  return `ещё ${n} организаций`
}

/** Пункты выбора представителя: «Не указан» и «Новое контактное лицо…» (у Radix Select нет пустого). */
const NO_REPRESENTATIVE = '__none__'
const NEW_REPRESENTATIVE = '__new__'

/**
 * Представитель организации у входа: выбор из контактных лиц организации или новое контактное
 * лицо (ФИО и должность) — оно добавляется в «Контакты» организации.
 */
function RepresentativeControl({
  cid,
  login,
  contacts,
  onContactCreated,
  onChange,
}: {
  cid: string
  login: ClientLogin
  contacts: Contact[]
  onContactCreated: (c: Contact) => void
  onChange: (next: ClientLogin[]) => void
}) {
  const [creating, setCreating] = useState(false)
  const [fullName, setFullName] = useState('')
  const [position, setPosition] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save(contactId: string): Promise<boolean> {
    setError(null)
    setBusy(true)
    try {
      onChange((await teamApi.setLoginRepresentative(cid, login.id, contactId)).items)
      return true
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить представителя'))
      return false
    } finally {
      setBusy(false)
    }
  }

  async function createAndSave(e: FormEvent) {
    e.preventDefault()
    if (!fullName.trim()) {
      setError('Укажите ФИО представителя')
      return
    }
    setError(null)
    setBusy(true)
    let contact: Contact
    try {
      contact = await teamClientApi(cid).createContact({
        fullName: fullName.trim(),
        position: position.trim(),
        phone: '',
        email: '',
        receivesNotifications: false,
      })
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось добавить контактное лицо'))
      setBusy(false)
      return
    }
    // Контакт уже создан: форма закрывается сразу — повторное нажатие не создаст дубль, а если
    // сохранить представителя не удалось, контакт можно выбрать в списке.
    onContactCreated(contact)
    setCreating(false)
    setFullName('')
    setPosition('')
    await save(contact.id)
  }

  return (
    <div className="mt-2 w-full max-w-md">
      <div className="mb-1 text-12 text-txt-secondary" aria-hidden="true">
        Представитель организации
      </div>
      <Select
        value={creating ? NEW_REPRESENTATIVE : (login.representative?.contactId ?? NO_REPRESENTATIVE)}
        disabled={busy}
        onValueChange={(v) => {
          if (v === NEW_REPRESENTATIVE) {
            setCreating(true)
            return
          }
          setCreating(false)
          void save(v === NO_REPRESENTATIVE ? '' : v)
        }}
      >
        <SelectTrigger aria-label={`Представитель организации: ${login.email}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_REPRESENTATIVE}>Представитель не указан</SelectItem>
          {/* Назначенный представитель виден, даже пока список контактов не загружен. */}
          {login.representative && !contacts.some((c) => c.id === login.representative?.contactId) ? (
            <SelectItem value={login.representative.contactId}>{login.representative.fullName}</SelectItem>
          ) : null}
          {contacts.map((c) => (
            <SelectItem key={c.id} value={c.id} hint={c.position || undefined}>
              {c.fullName}
            </SelectItem>
          ))}
          <SelectItem value={NEW_REPRESENTATIVE}>Новое контактное лицо…</SelectItem>
        </SelectContent>
      </Select>
      {creating ? (
        <form className="mt-2 flex flex-col gap-2" onSubmit={createAndSave} noValidate>
          <Field label="ФИО представителя" required className="my-0">
            {(p) => <Input {...p} value={fullName} onChange={(e) => setFullName(e.target.value)} />}
          </Field>
          <Field label="Должность" className="my-0">
            {(p) => <Input {...p} value={position} onChange={(e) => setPosition(e.target.value)} />}
          </Field>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={busy}>
              {busy ? 'Сохранение…' : 'Добавить и выбрать'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setCreating(false)
                setError(null)
              }}
            >
              Отмена
            </Button>
          </div>
        </form>
      ) : null}
      {error ? (
        <Alert variant="danger" className="mt-2">
          {error}
        </Alert>
      ) : null}
    </div>
  )
}

export interface ClientLoginsPanelProps {
  cid: string
  logins: ClientLogin[]
  /** Список изменился (закрепили или открепили вход). */
  onChange: (next: ClientLogin[]) => void
}

export function ClientLoginsPanel({ cid, logins, onChange }: ClientLoginsPanelProps) {
  const [email, setEmail] = useState('')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unlinking, setUnlinking] = useState<ClientLogin | null>(null)
  const [busy, setBusy] = useState(false)
  // Контактные лица организации — варианты представителя у входов.
  const [contacts, setContacts] = useState<Contact[]>([])
  const [contactsError, setContactsError] = useState<string | null>(null)
  useEffect(() => {
    const controller = new AbortController()
    setContactsError(null)
    teamClientApi(cid)
      .listContacts(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setContacts(res.items)
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setContactsError(getErrorMessage(err, 'Не удалось загрузить контактных лиц организации'))
        }
      })
    return () => controller.abort()
  }, [cid])

  async function link(e: FormEvent) {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) {
      setError('Укажите почту входа')
      return
    }
    setError(null)
    setAdding(true)
    try {
      const res = await teamApi.linkClientLogin(cid, trimmed)
      onChange(res.items)
      setEmail('')
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось закрепить вход'))
    } finally {
      setAdding(false)
    }
  }

  async function unlink() {
    if (!unlinking) return
    setError(null)
    setBusy(true)
    try {
      const res = await teamApi.unlinkClientLogin(cid, unlinking.id)
      onChange(res.items)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось открепить вход'))
    } finally {
      setBusy(false)
      setUnlinking(null)
    }
  }

  return (
    <Panel className="p-5">
      <h3 className="mb-1 font-semibold text-txt">Доступ к организации</h3>
      <p className="mb-4 max-w-2xl text-txt-secondary">
        Вход специалиста, который ведёт несколько организаций, можно закрепить за этой организацией:
        после входа он выбирает, в какую зайти. Закреплённый вход открепляется отсюда — доступ к этой
        организации пропадает сразу, остальные организации входа не затрагиваются. У каждого входа
        можно указать представителя организации — его ФИО и должность клиент видит справа вверху
        кабинета.
      </p>
      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      {contactsError && logins.length > 0 ? (
        <Alert variant="danger" className="mb-3">
          {contactsError} — выбрать представителя пока нельзя.
        </Alert>
      ) : null}
      {logins.length === 0 ? (
        <p className="mb-4 text-txt-secondary">
          У организации нет ни одного входа. Задайте вход выше или закрепите существующий.
        </p>
      ) : (
        <ul className="mb-5 max-w-2xl divide-y divide-line rounded-12 border border-line">
          {logins.map((login) => (
            <li key={login.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="truncate font-medium text-txt">{login.email}</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {login.primary ? <Badge variant="blue">Основной вход организации</Badge> : null}
                  {login.disabled ? <Badge variant="red">Отключён</Badge> : null}
                  {login.otherOrganizations > 0 ? (
                    <Badge variant="gray">{otherOrganizationsLabel(login.otherOrganizations)}</Badge>
                  ) : null}
                </div>
                <RepresentativeControl
                  cid={cid}
                  login={login}
                  contacts={contacts}
                  onContactCreated={(c) => setContacts((list) => [...list, c])}
                  onChange={onChange}
                />
              </div>
              <Button variant="secondary" size="sm" onClick={() => setUnlinking(login)}>
                Открепить
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form className="max-w-md" onSubmit={link} noValidate>
        <Field
          label="Закрепить существующий вход"
          hint="Почта входа в личный кабинет другой организации, которую ведёт тот же специалист"
        >
          {(p) => (
            <Input
              {...p}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="specialist@company.ru"
              autoComplete="off"
            />
          )}
        </Field>
        <Button type="submit" variant="primary" disabled={adding || email.trim() === ''}>
          {adding ? 'Закрепляем…' : 'Закрепить'}
        </Button>
      </form>
      <ConfirmDialog
        open={unlinking !== null}
        title="Открепить вход от организации?"
        message={
          unlinking
            ? `Вход ${unlinking.email} потеряет доступ к этой организации сразу. Остальные организации входа не затрагиваются.`
            : undefined
        }
        buttons={[
          { label: busy ? 'Открепляем…' : 'Открепить', variant: 'primary', disabled: busy, onClick: () => void unlink() },
          { label: 'Отмена', variant: 'secondary', disabled: busy, onClick: () => setUnlinking(null) },
        ]}
        onClose={() => setUnlinking(null)}
        closeDisabled={busy}
      />
    </Panel>
  )
}
